import React, { useEffect, useState } from 'react';
import { Grid, Link, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useMainLayoutContext } from '../../layouts/MainLayoutContext';
import { useAuth } from '../../contexts/AuthContext';
import { GTypography } from '../../components';
import { ROUTES } from '../../consts/route.const';
import { Document } from '../../@types';
import { AxiosClient } from '../../utils';

const linkProps: any = {
  component: 'span',
  underline: 'hover',
  sx: { cursor: 'pointer', fontWeight: 'bold' },
};

export const ExampleProjectsListPage = () => {
  const navigate = useNavigate();
  const { updateContextValue } = useMainLayoutContext();
  const { user, values } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const { data } = await AxiosClient.get('/documents');
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    updateContextValue({ title: 'Example Projects' });

    return () => {
      updateContextValue({ title: undefined });
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user?.projects && user.projects.length > 0) {
      fetchDocuments();
    }
  }, [user?.projects]);

  if (!values.ready) {
    return (
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <GTypography color="textSecondary" lineHeight={2} fontWeight={500}>
            Loading...
          </GTypography>
        </Grid>
      </Grid>
    );
  }

  if (!user?.projects || user.projects.length === 0) {
    return (
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <GTypography color="textSecondary" lineHeight={2} fontWeight={500}>
            You need to create a project first to access example projects. Click on{' '}
            <Link
              {...linkProps}
              onClick={() =>
                navigate(
                  `/${ROUTES.PROJECTS}/${ROUTES.INTRO}/${ROUTES.MODULE_OBJECTIVES}`
                )
              }
            >
              CREATE NEW PROJECT
            </Link>{' '}
            to start an evaluation plan for a new project.
          </GTypography>
        </Grid>
      </Grid>
    );
  }

  const handleDownload = async (downloadUrl: string, filename: string) => {
    try {
      const response = await AxiosClient.get(downloadUrl.replace('http://localhost:8888/api/v1', ''), {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} container spacing={1}>
        <Grid item xs={12}>
          <GTypography color="textSecondary" lineHeight={2} fontWeight={500}>
            Browse our collection of example projects to help guide your planning
            and evaluation process.
          </GTypography>
        </Grid>

        {loading && (
          <Grid item xs={12}>
            <GTypography color="textSecondary" lineHeight={2} fontWeight={500}>
              Loading documents...
            </GTypography>
          </Grid>
        )}

        {!loading && documents.length === 0 && (
          <Grid item xs={12}>
            <GTypography color="textSecondary" lineHeight={2} fontWeight={500}>
              No example projects available at this time.
            </GTypography>
          </Grid>
        )}

        {!loading && documents.length > 0 && (
          <Grid item xs={12}>
            <GTypography color="textSecondary" lineHeight={2} fontWeight={500} sx={{ mb: 2 }}>
              Available Documents:
            </GTypography>
            {documents.map((doc: Document) => (
              <Box key={doc.id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <GTypography variant="h6" fontWeight={600}>
                      {doc.name}
                    </GTypography>
                    {doc.description && (
                      <GTypography color="textSecondary" sx={{ mt: 1 }}>
                        {doc.description}
                      </GTypography>
                    )}
                    <GTypography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Size: {doc.file_size_human}
                    </GTypography>
                  </Grid>
                  <Grid item xs={12} sm={4} textAlign={{ xs: 'left', sm: 'right' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleDownload(doc.download_url, doc.filename)}
                      size="small"
                    >
                      Download PDF
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};